import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for lazy-loading components using Intersection Observer API
 * Returns a ref to attach to the component and a boolean indicating visibility
 * 
 * @param {Object} options - Options for Intersection Observer
 * @param {number} options.threshold - Percentage of element visible before triggering (0-1, default: 0.1)
 * @param {string} options.rootMargin - Margin around root for early loading (default: "0px")
 * @returns {Array} [ref, isVisible] - ref to attach to component, boolean visibility state
 * 
 * @example
 * const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });
 * return (
 *   <div ref={ref}>
 *     {isVisible ? <ExpensiveComponent /> : <div>Loading...</div>}
 *   </div>
 * );
 */
export default function useIntersectionObserver(options = {}) {
  const {
    threshold = 0.1,
    rootMargin = "0px"
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if Intersection Observer is supported (server-side rendering safety)
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    // If element was already intersected before, don't create observer
    // This prevents re-fetching after lazy-load
    if (isVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Unobserve after first intersection to prevent re-loading
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, rootMargin, isVisible]);

  return [ref, isVisible];
}
